<template>
  <div class="blik-page">
    <div class="blik-page__wrap">
      <h1 class="blik-page__title">{{ $t('blik__title') }}</h1>
      <p class="blik-page__intro">{{ $t('blik__intro') }}</p>

      <div v-if="!hideInvoiceLifetimeCounter" class="blik-page__timer">
        {{ $t('blik__time_remaining') }}:
        <span :class="formatedTime.class" class="blik-page__timer-value">
          {{ formatedTime.time }}
        </span>
      </div>

      <div class="blik-page__banner">
        <svg
          class="blik-page__banner-icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 3.5C8.55228 3.5 9 3.94772 9 4.5C9 5.05228 8.55228 5.5 8 5.5C7.44772 5.5 7 5.05228 7 4.5C7 3.94772 7.44772 3.5 8 3.5ZM9.5 12H6.5V11H7.5V7.5H6.5V6.5H8.5V11H9.5V12Z"
            fill="#0020ff" />
        </svg>
        <span>{{ $t('blik__info_banner') }}</span>
      </div>

      <div class="blik-page__field">
        <div class="blik-page__field-label blik-page__field-label--center">
          {{ $t('blik__amount_label') }}
        </div>
        <div class="blik-page__amount">
          <span v-if="templateData.symbol" class="blik-page__amount-symbol">
            {{ templateData.symbol }}
          </span>
          <span class="blik-page__amount-value">{{ templateData.amount }}</span>
          <span v-if="!templateData.symbol" class="blik-page__amount-currency">
            {{ templateData.currency }}
          </span>
          <ButtonCopy :value="`${templateData.amount} ${templateData.currency}`" />
        </div>
      </div>

      <div class="blik-page__field">
        <div class="blik-page__field-label">{{ $t('blik__phone_label') }}</div>
        <div class="blik-page__input">
          <span>{{ templateData.customer_phone }}</span>
          <ButtonCopy :value="templateData.customer_phone" />
        </div>
      </div>

      <div class="blik-page__field">
        <div class="blik-page__field-label">{{ $t('blik__full_name_label') }}</div>
        <div class="blik-page__input">
          <span>{{ templateData.full_name }}</span>
          <ButtonCopy :value="templateData.full_name" />
        </div>
      </div>

      <div class="blik-page__steps">
        <div v-for="(step, index) in steps" :key="index" class="blik-page__step">
          <div class="blik-page__step-number">{{ index + 1 }}</div>
          <div class="blik-page__step-label" v-html="$t(step.key, step.params)" />
        </div>
      </div>

      <div class="blik-page__note">
        {{ $t('blik__after_transfer') }}
      </div>

      <ul class="blik-page__notes">
        <li>{{ $t('blik__note_1') }}</li>
        <li>{{ $t('blik__note_2') }}</li>
      </ul>

      <button :disabled="loading" class="blik-page__btn blik-page__btn--primary" @click="confirm">
        <div v-if="loading" class="blik-page__btn-loader">
          <Loader />
        </div>
        {{ $t('blik__btn_confirm') }}
      </button>

      <button
        v-if="templateData.has_return_url"
        class="blik-page__btn blik-page__btn--secondary"
        @click="redirectReturnUrl(true)">
        {{ $t('blik__btn_cancel') }}
      </button>
    </div>

    <div class="blik-page__footer">
      <div class="blik-page__footer-locale">
        <img src="./assets/locale.svg" alt="" />
        <div
          v-for="(locale, index) in locales"
          :key="index"
          :class="{ active: locale === currentLocale }"
          class="blik-page__footer-locale__item"
          @click="selectLocale(locale)">
          {{ locale.toUpperCase() }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ButtonCopy from './components/ButtonCopy.vue';
import Loader from './components/Loader.vue';
import { createDayJsDurationFormatterFactory } from '@/helpers/DayJsDurationFormatter/DayJsDurationFormatterImpl';
import { messages } from './translations/index';
import parseAvailableLocales from '@/helpers/parseAvailableLocales';

const min30 = 1800000;
const min15 = 900000;

export default {
  components: { ButtonCopy, Loader },

  created() {
    this.startTimer(this.templateData.expires);

    if (this.locales.includes(this.selectedLocale)) {
      this.selectLocale(this.selectedLocale);
    } else {
      this.selectLocale(this.locales.length ? this.locales[0] : 'en');
    }
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData', 'checkoutOptions']),
    ...mapState('InvoiceLifetime', ['isExpired', 'timeToExpired']),
    ...mapState('UI', ['availableLocales', 'queryLocale', 'layoutConfig']),

    hideInvoiceLifetimeCounter() {
      const config = this.layoutConfig;
      return config &&
        typeof config === 'object' &&
        typeof config.hide_invoice_lifetime_counter === 'boolean'
        ? config.hide_invoice_lifetime_counter
        : false;
    },

    locales() {
      const optionLocales = parseAvailableLocales(
        this.availableLocales,
        this.checkoutOptions?.available_locales,
        Object.keys(messages)
      );

      if (optionLocales.length) {
        return optionLocales;
      }

      return Object.keys(messages);
    },

    selectedLocale() {
      const { default_locale } = this.paymentInvoice;

      const supportedLocale = locale =>
        Object.keys(messages).some(key => key === locale) ? locale : null;

      return (
        supportedLocale(default_locale) ||
        supportedLocale(this.queryLocale) ||
        supportedLocale(this.checkoutOptions?.locale) ||
        supportedLocale(this.$browserLocale)
      );
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    dateFormatter() {
      return createDayJsDurationFormatterFactory({
        defaultTemplate: 'HH:mm:ss',
      });
    },

    formatedTime() {
      const time = this.dateFormatter().format(this.timeToExpired);

      const classTime =
        this.timeToExpired > min30
          ? null
          : this.timeToExpired <= min30 && this.timeToExpired > min15
          ? 'medium'
          : 'low';

      return {
        time,
        class: classTime,
      };
    },

    steps() {
      const { customer_phone, amount, currency } = this.templateData;
      const list = [
        { key: 'blik__step_1', params: {} },
        { key: 'blik__step_2', params: {} },
        { key: 'blik__step_3', params: { customer_phone } },
        { key: 'blik__step_4', params: {} },
        { key: 'blik__step_5', params: { amount, currency } },
        { key: 'blik__step_6', params: {} },
      ];

      return list;
    },

    templateData() {
      const { has_return_url, id, expires } = this.paymentInvoice;

      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        requisites,
        amount: payloadAmount,
        currency: payloadCurrency,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }
        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }
        return requisites;
      };

      const { customer_phone = '', full_name = '' } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;
      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        has_return_url,
        expires,
        customer_phone: customer_phone || '',
        full_name: full_name || '',
        amount: amount != null ? amount.toString() : '',
        currency: currency || '',
        symbol: this.currencySymbol(currency),
      };
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),
    ...mapActions('InvoiceLifetime', ['startTimer']),

    selectLocale(locale) {
      this.$bus.$emit('changeLocale', locale);
    },

    async confirm() {
      if (!this.loading) {
        this.loading = true;
        const { status } = await this.confirmPaymentInvoice({
          id: this.templateData.id,
          data: {},
        });

        if (status === 200) {
          this.redirectReturnUrl();
        } else {
          this.loading = false;
        }
      }
    },

    redirectReturnUrl(force) {
      if (!this.templateData.has_return_url) {
        return;
      }

      const isForce = force ? '&force=true' : '';
      location.replace(`/return?id=${this.paymentInvoice.id}${isForce}`);
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'PLN':
          return 'zł';
        case 'UAH':
          return '₴';
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
        case 'RUB':
          return '₽';
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

$--blue-color: #0020ff;
$--blue-hover: #334dff;
$--blue-active: #001acc;
$--blue-disabled: #d6daf5;
$--blue-light: #f1f3ff;
$--blue-banner-bg: #eef0ff;

$--text-primary: #334150;
$--text-secondary: #7f8fa4;
$--white: #ffffff;

.blik-page {
  display: flex;
  align-items: center;
  flex-direction: column;
  font-family: 'Roboto', sans-serif;

  &__wrap {
    max-width: 540px;
    width: 100%;
    padding: 32px;
    box-shadow: 0px 50px 60px -30px rgba(50, 74, 101, 0.15);
    border-radius: 10px;
    background: $--white;

    @media (max-width: 480px) {
      padding: 24px 20px;
    }
  }

  // Title
  &__title {
    text-align: center;
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    color: $--text-primary;
    margin: 0 0 8px;
  }

  // Intro
  &__intro {
    text-align: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $--text-secondary;
    margin: 0 0 20px;
  }

  // Timer
  &__timer {
    text-align: center;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: $--text-primary;
    margin-bottom: 20px;

    &-value {
      &.low {
        color: #e82a41;
      }
      &.medium {
        color: #ffbe17;
      }
    }
  }

  // Info banner
  &__banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: $--blue-banner-bg;
    border-left: 3px solid $--blue-color;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 24px;

    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: $--text-primary;

    &-icon {
      flex-shrink: 0;
      margin-top: 1px;
    }
  }

  // Generic field
  &__field {
    margin-bottom: 16px;
  }

  &__field-label {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: $--text-secondary;
    margin-bottom: 6px;
    text-transform: uppercase;
    text-align: left;

    &--center {
      text-align: center;
    }
  }

  &__input {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: $--blue-light;
    border: 1.5px solid #e1e8ee;
    border-radius: 8px;
    padding: 8px 16px;
    gap: 12px;
    transition: all 0.3s;

    span {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 1.5px;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
    }

    .button-copy {
      flex-shrink: 0;
    }
  }

  // Amount
  &__amount {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.5px;
    color: $--text-primary;

    &-symbol,
    &-currency {
      font-weight: 500;
      font-size: 16px;
      color: $--text-secondary;
    }
  }

  // Steps
  &__steps {
    margin: 24px 0 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__step {
    display: grid;
    grid-template-columns: 24px auto;
    gap: 10px;
    align-items: flex-start;

    &-number {
      width: 24px;
      height: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: $--blue-light;
      color: $--blue-color;
      border-radius: 50%;
      font-weight: 700;
      font-size: 11px;
      line-height: 16px;
      flex-shrink: 0;
    }

    &-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 24px;
      color: $--text-primary;
      text-align: left;

      b {
        font-weight: 600;
      }
    }
  }

  &__note {
    background: #f8f9fc;
    border-radius: 6px;
    padding: 12px 16px;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
    color: $--text-secondary;
    margin-bottom: 12px;
  }

  &__notes {
    margin: 0 0 16px;
    padding-left: 18px;

    li {
      font-weight: 400;
      font-size: 13px;
      line-height: 18px;
      color: $--text-secondary;
      list-style-type: disc;
      text-align: left;
    }
  }

  &__btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 340px;
    margin: 0 auto;
    padding: 14px 16px;
    border-radius: 6px;
    border: none;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: background 0.3s, border-color 0.3s, color 0.3s;
    position: relative;
    overflow: hidden;

    &--primary {
      background: $--blue-color;
      color: $--white;
      margin-top: 24px;

      &:hover {
        background: $--blue-hover;
      }

      &:active {
        background: $--blue-active;
      }

      &:disabled {
        background: $--blue-disabled;
        cursor: not-allowed;
      }
    }

    &--secondary {
      background: transparent;
      color: $--blue-color;
      border: 1px solid $--blue-color;
      margin-top: 12px;

      &:hover {
        background: $--blue-light;
      }

      &:active {
        background: darken($--blue-light, 5%);
      }
    }

    &-loader {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(0, 0, 0, 0.43);
    }
  }

  &__footer {
    max-width: 540px;
    width: 100%;
    margin: 16px 0;
    display: flex;
    justify-content: flex-end;

    &-locale {
      display: flex;
      align-items: center;
      gap: 4px;

      img {
        height: 14px;
        margin-right: 4px;
      }

      &__item {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: $--text-secondary;
        transition: color 0.3s;
        border-right: 1px solid #cdd5db;
        cursor: pointer;
        padding: 0 8px;

        &.active,
        &:hover {
          color: $--blue-color;
        }

        &:last-child {
          border-right: none;
          padding-right: 0;
        }
      }
    }
  }
}
</style>
